import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faFloppyDisk,
  faMagnifyingGlass,
  faList,
  faGraduationCap,
  faSchool,
  faLock,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { APIMS } from "../../../../../../../config/apims";
import { APILA } from "../../../../../../../config/apila";
import ToastSuccess from "../../../../../../NotificationToast/ToastSuccess";
import ToastError from "../../../../../../NotificationToast/ToastError";
import "bulma/css/bulma.css";
import "../../../../../../../index.css";
import "./list-perilaku-add.css";

const unpack = (value) =>
  Array.isArray(value) ? value : Array.isArray(value?.data) ? value.data : [];

const rank = (name = "") =>
  /^(SD|MI)/i.test(name)
    ? 0
    : /^(SMP|MTs)/i.test(name)
    ? 1
    : /^(SMA|SMK|MA)/i.test(name)
    ? 2
    : 3;

// Response daftar indikator dan data tabel memakai alias ID yang berbeda.
const indicatorId = (item) =>
  item?.ysb_sub_element_id || item?.id_sub_element || item?.sub_element_id || item?.id || "";

const sameIndicator = (a, b) => {
  const aId = indicatorId(a);
  const bId = indicatorId(b);
  // GET tabel terkadang menyimpan ID relasi berbeda dari ID master indikator.
  return Boolean(aId && bId && String(aId) === String(bId)) ||
    indicatorName(a).trim().toLowerCase() === indicatorName(b).trim().toLowerCase();
};

// Respons GET list perilaku bisa memakai `id` sebagai ID relasi, bukan ID indikator.
// Hanya bandingkan ID master indikator yang eksplisit. Jika tidak tersedia,
// gunakan nama beserta elemen/dimensi agar indikator yang berbeda tidak tertukar.
const usedIndicatorMatches = (option, used) => {
  const masterFields = ["ysb_sub_element_id", "id_sub_element", "sub_element_id"];
  const optionIds = masterFields.map((key) => option?.[key]).filter(Boolean);
  if (option?.id) optionIds.push(option.id); // ID master dari GET /sub-elements
  const usedIds = masterFields.map((key) => used?.[key]).filter(Boolean);
  if (usedIds.length && optionIds.length &&
      usedIds.some((id) => optionIds.some((value) => String(value) === String(id)))) return true;
  if (usedIds.length && optionIds.length) return false;

  const name = (value) => indicatorName(value).trim().toLowerCase();
  if (!name(option) || !name(used) || name(option) !== name(used)) return false;
  const dimensions = ["ysb_dimensi_id", "dimensi_id", "name_dimensi"];
  const elements = ["ysb_element_id", "element_id", "id_element", "name_element"];
  const different = (fields) => fields.some((key) =>
    option?.[key] && used?.[key] && String(option[key]).toLowerCase() !== String(used[key]).toLowerCase()
  );
  return !different(dimensions) && !different(elements);
};

const groupIdentity = (item) => item?.group_id || item?.indikator?.[0]?.group_id ||
  item?.config_anomaly_id || item?.ysb_config_anomaly_id || item?.id || "";

const asIds = (value) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map((item) => typeof item === "object" ? (item?.school_code || item?.ysb_school_id || item?.ysb_semester_id || item?.id || "") : item)
    .filter(Boolean);

const findOption = (options, desiredId, desiredName, idFields, nameFields) =>
  options.find((item) =>
    idFields.some((field) => desiredId && String(item?.[field]) === String(desiredId)) ||
    nameFields.some((field) => desiredName &&
      String(item?.[field] || "").trim().toLowerCase() === String(desiredName).trim().toLowerCase())
  );

const indicatorName = (item) =>
  item?.name_sub_element ||
  item?.sub_element ||
  item?.name_indicator ||
  item?.indicator ||
  item?.name ||
  item?.description ||
  "Indikator";

// Dipakai bersama oleh modal Add dan Update agar tampilan tetap identik.
export default function ListPerilakuAdd({ show, onHide, onSaved, editData = null, context = null }) {
  const isEdit = Boolean(editData);
  // ID konfigurasi berasal dari baris GET /api/config-anomalys/multiple-indikator
  // yang diklik pengguna, bukan dari ID indikator atau filter.
  const updateConfigAnomalyId = editData?.ysb_config_anomaly_id ||
    editData?.config_anomaly_id || editData?.id || "";
  const oldIndicators = Array.isArray(editData?.indikator) ? editData.indikator : [];
  const firstOldIndicator = oldIndicators[0] || {};
  const oldElementId = editData?.ysb_element_id || editData?.id_element || editData?.element_id || firstOldIndicator?.ysb_element_id || firstOldIndicator?.id_element;
  const oldElementName = editData?.name_element || firstOldIndicator?.name_element;
  const token = localStorage.getItem("token");
  const userBranch = localStorage.getItem("ysb_branch_id") || "ALL";
  const userLevel = localStorage.getItem("level") || "ALL";
  const userId = (localStorage.getItem("id_admin") || "").replace(
    /[|&;$%@"<>()+,]/g,
    ""
  );

  const headers = useMemo(
    () => ({ headers: { Authorization: token, "Content-Type": "application/json" } }),
    [token]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [semesters, setSemesters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [schools, setSchools] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [semesterIds, setSemesterIds] = useState([]);
  const [description, setDescription] = useState("");
  const [elements, setElements] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [elementId, setElementId] = useState("");
  const [chosen, setChosen] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [dropdown, setDropdown] = useState(false);
  const [loadingElement, setLoadingElement] = useState(false);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  // GET per kombinasi sekolah/semester: tidak menggabungkan pemakaian seluruh sekolah.
  const [usageState, setUsageState] = useState({ signature: "", indicatorsByTarget: {}, error: false });
  const [checkingUsed, setCheckingUsed] = useState(false);
  const [lookupRevision, setLookupRevision] = useState(0);
  // Cache per kombinasi tahun/semester/sekolah selama modal terbuka.
  // Kombinasi yang sudah diperiksa tidak perlu diminta ulang.
  const usageCache = useRef(new Map());

  const dropdownRef = useRef(null);
  const elementSequence = useRef(0);
  const indicatorSequence = useRef(0);

  const active = useMemo(
    () => (isEdit && (
      semesters.find((semester) => String(semester.id) === String(asIds(editData?.ysb_semester_id)[0])) ||
      semesters.find((semester) => String(semester.ysb_academic_year_id) === String(editData?.ysb_academic_year_id))
    )) || semesters.find((semester) => Number(semester.status) === 1) || semesters[0],
    [semesters, isEdit, editData]
  );
  const academicId = active?.ysb_academic_year_id || editData?.ysb_academic_year_id || "";
  const year = active?.name_year || "";

  const semestersInYear = useMemo(
    () =>
      semesters
        .filter((semester) => semester.name_year === year)
        .sort((a, b) => Number(a.semester) - Number(b.semester)),
    [semesters, year]
  );

  // Daftar sekolah di modal mengikuti cabang yang dipilih, terpisah dari filter Home.
  const visibleSchools = useMemo(
    () =>
      schools
        .filter((school) =>
          school.school_code !== "BASEMENT_BEKASI" &&
          String(school.ysb_branch_id) === String(branchId)
        )
        .sort(
          (a, b) =>
            rank(a.school_name) - rank(b.school_name) ||
            String(a.school_name).localeCompare(String(b.school_name), "id")
        ),
    [schools, branchId]
  );

  // Semester dan cabang diambil sekali. Sekolah diambil terpisah setiap kali
  // cabang berubah supaya pilihan sekolah tidak tercampur antar cabang.
  useEffect(() => {
    if (!show) return;
    let alive = true;
    setLoading(true);

    Promise.all([
      APIMS.get("/api/semesters?page=1&limit=1000&ascending=0&search=", headers),
      APIMS.get(
        `/api/access/branches?page=1&limit=1000&ascending=1&search=&branch=${encodeURIComponent(userBranch)}&level=${encodeURIComponent(userLevel)}`,
        headers
      ),
    ])
      .then(([semesterResponse, branchResponse]) => {
        if (!alive) return;
        const semesterData = unpack(semesterResponse.data);
        const branchData = unpack(branchResponse.data);
        const current = semesterData.find((semester) => Number(semester.status) === 1) || semesterData[0];

        setSemesters(semesterData);
        setBranches(branchData);
        const originalBranch = isEdit ? editData?.ysb_branch_id : context?.branchId;
        const suggestedBranch = originalBranch || (userBranch !== "ALL" ? userBranch : "");
        const matchingBranch = branchData.find((branch) =>
          String(branch.branch_code) === String(suggestedBranch)
        );
        setBranchId(matchingBranch?.branch_code || branchData[0]?.branch_code || "");
        setSchools([]);

        const editSemesterIds = asIds(editData?.ysb_semester_id);
        const editSchoolId = asIds(editData?.ysb_school_id || editData?.school_code || editData?.school)[0] || "";
        // Add: semester 1 dan 2 dari tahun ajaran aktif otomatis dicentang.
        // Update: hanya semester milik data yang diedit.
        const defaultSemesterIds = semesterData
          .filter((semester) =>
            semester.name_year === current?.name_year &&
            [1, 2].includes(Number(semester.semester))
          )
          .sort((a, b) => Number(a.semester) - Number(b.semester))
          .map((semester) => semester.id);
        setSemesterIds(
          isEdit
            ? editSemesterIds.slice(0, 1)
            : defaultSemesterIds.length ? defaultSemesterIds : current?.id ? [current.id] : []
        );
        setSchoolId(isEdit ? editSchoolId : "");
        setDescription(isEdit ? editData?.description_anomaly || "" : "");
        setChosen(isEdit ? oldIndicators : []);
      })
      .catch((error) => {
        if (alive) {
          ToastError.fire({
            icon: "error",
            title: error.response?.data?.message || "Gagal memuat semester dan cabang.",
          });
        }
      })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [show, headers, userBranch, userLevel, isEdit, editData, context?.branchId]);

  useEffect(() => {
    if (!show || !branchId) {
      setSchools([]);
      setLoadingSchools(false);
      return;
    }
    let alive = true;
    setLoadingSchools(true);
    setSchools([]);
    APIMS.get(
      `/api/access/schools?page=1&limit=1000&ascending=1&branch=${encodeURIComponent(branchId)}&level=${encodeURIComponent(userLevel)}`,
      headers
    )
      .then((response) => {
        if (!alive) return;
        const branchSchools = unpack(response.data).filter((school) =>
          String(school.ysb_branch_id) === String(branchId) &&
          school.school_code !== "BASEMENT_BEKASI"
        );
        setSchools(branchSchools);
        // Tidak menghapus pilihan indikator saat cabang/sekolah berubah.
        // Validasi sekolah yang lama supaya tidak ikut terkirim dari cabang lain.
        setSchoolId((previous) =>
          branchSchools.some((school) => String(school.school_code) === String(previous))
            ? previous : ""
        );
      })
      .catch((error) => {
        if (!alive) return;
        setSchools([]);
        setSchoolId("");
        ToastError.fire({
          icon: "error",
          title: error.response?.data?.message || "Gagal memuat sekolah pada cabang ini.",
        });
      })
      .finally(() => { if (alive) setLoadingSchools(false); });
    return () => { alive = false; };
  }, [show, branchId, userLevel, headers]);

  const selectedSchool = visibleSchools.find((school) =>
    String(school.school_code) === String(schoolId)
  );
  const selectedSemesterInfo = semestersInYear.find((semester) =>
    String(semester.id) === String(semesterIds[0] || "")
  );
  const editSchoolCode = schoolId || "";
  const editSchoolName = selectedSchool?.school_name || editData?.school_name ||
    editData?.name_school || editSchoolCode || "Sekolah tidak ditemukan";
  const editBranchCode = selectedSchool?.ysb_branch_id || editData?.ysb_branch_id || "";

  const usageTargets = useMemo(() => {
    if (!academicId || !semesterIds.length || !schoolId || !branchId || !selectedSchool) return [];
    return semesterIds.flatMap((semesterId) => {
      const semester = semestersInYear.find((item) => String(item.id) === String(semesterId));
      if (!semester) return [];
      return [{
        yearId: semester.ysb_academic_year_id,
        semesterId: semester.id,
        schoolId: selectedSchool.school_code,
        branchId,
        key: [semester.ysb_academic_year_id, semester.id, branchId, selectedSchool.school_code].join("|"),
      }];
    });
  }, [academicId, semesterIds, schoolId, branchId, selectedSchool, semestersInYear]);

  // Setiap semester pada satu sekolah diperiksa secara terpisah.
  // Gunakan signature agar hasil GET lama tidak dapat dipakai untuk menyimpan
  // setelah user mengganti pilihan sekolah/semester saat request masih berlangsung.
  const usageSignature = usageTargets.map((target) => target.key).sort().join("||");
  const usageReady = Boolean(usageSignature) && !checkingUsed &&
    !usageState.error && usageState.signature === usageSignature;
  const usedLookupError = usageState.error && usageState.signature === usageSignature;

  useEffect(() => {
    let alive = true;
    setCheckingUsed(Boolean(show && usageTargets.length));
    if (!show || !usageTargets.length) {
      setUsageState({ signature: "", indicatorsByTarget: {}, error: false });
      return () => { alive = false; };
    }

    const loadTarget = (target) => {
      const cached = usageCache.current.get(target.key);
      if (cached) return cached;
      const payloadBase = {
        limit: 1000, ascending: true, search: "", level: "user",
        ysb_academic_year_id: target.yearId,
        ysb_semester_id: target.semesterId,
        ysb_branch_id: target.branchId,
        ysb_school_id: target.schoolId,
      };
      const pending = (async () => {
        // Minta halaman selanjutnya bila total mencapai 1000, agar indikator
        // yang ada di halaman lain tidak dianggap belum digunakan.
        let page = 1;
        const allRows = [];
        while (page <= 100) {
          const payload = { ...payloadBase, page };
          const response = await APILA.request({
            ...headers,
            method: "GET",
            url: "/api/config-anomalys/multiple-indikator",
            params: payload,
            data: payload,
          });
          const entries = unpack(response.data);
          allRows.push(...entries);
          if (entries.length < payloadBase.limit) break;
          page += 1;
        }
        if (page > 100) throw new Error("Data list perilaku terlalu banyak untuk diperiksa.");
        // Jika metadata pembeda tidak ada, jangan menganggap data kosong.
        // Itu bisa menyebabkan duplikasi di sekolah lain.
        if (allRows.some((row) => !row?.ysb_academic_year_id ||
          !row?.ysb_semester_id || !row?.ysb_branch_id || !row?.ysb_school_id)) {
          throw new Error("Respons API tidak menyertakan identitas sekolah/semester.");
        }
        return allRows.filter((row) =>
          String(row?.ysb_academic_year_id) === String(target.yearId) &&
          String(row?.ysb_semester_id) === String(target.semesterId) &&
          String(row?.ysb_branch_id) === String(target.branchId) &&
          String(row?.ysb_school_id) === String(target.schoolId)
        );
      })().catch((error) => {
        usageCache.current.delete(target.key);
        throw error;
      });
      usageCache.current.set(target.key, pending);
      return pending;
    };

    const check = async () => {
      try {
        const results = [];
        let next = 0;
        // Batasi request bersamaan saat ada beberapa semester.
        const workers = Array.from({ length: Math.min(4, usageTargets.length) }, async () => {
          while (next < usageTargets.length) {
            const index = next++;
            results[index] = await loadTarget(usageTargets[index]);
          }
        });
        await Promise.all(workers);
        if (!alive) return;
        const ownGroupId = groupIdentity(editData);
        const indicatorsByTarget = {};
        usageTargets.forEach((target, index) => {
          const otherRows = results[index].filter((row) =>
            !isEdit || !ownGroupId || String(groupIdentity(row)) !== String(ownGroupId)
          );
          indicatorsByTarget[target.key] = otherRows.flatMap((row) =>
            Array.isArray(row?.indikator) ? row.indikator : []
          );
        });
        setUsageState({ signature: usageSignature, indicatorsByTarget, error: false });
      } catch (_error) {
        if (alive) setUsageState({ signature: usageSignature, indicatorsByTarget: {}, error: true });
      } finally {
        if (alive) setCheckingUsed(false);
      }
    };
    check();
    return () => { alive = false; };
  }, [show, usageSignature, headers, isEdit, editData, lookupRevision]);

  // Jumlah pasangan sekolah + semester di mana indikator digunakan di list LAIN.
  const getIndicatorUsage = (indicator) => {
    const usedTargets = usageTargets.filter((target) =>
      (usageState.indicatorsByTarget[target.key] || []).some((used) =>
        usedIndicatorMatches(indicator, used)
      )
    );
    return {
      used: usedTargets.length,
      total: usageTargets.length,
      disabled: usageReady && usedTargets.length === usageTargets.length,
      usedTargets,
    };
  };

  // Satu sekolah per POST. Gabungkan semester menjadi array hanya jika
  // indikator yang boleh disimpan pada semester tersebut sama; bila berbeda,
  // kirim request terpisah agar indikator yang telah dipakai tidak terduplikasi.
  const buildStorePlan = () => {
    const groups = new Map();
    let skipped = 0;
    for (const target of usageTargets) {
      const used = usageState.indicatorsByTarget[target.key] || [];
      const missing = chosen.filter((indicator) =>
        !used.some((entry) => usedIndicatorMatches(indicator, entry))
      );
      skipped += chosen.length - missing.length;
      if (!missing.length) continue;
      const ids = [...new Set(missing.map((indicator) => String(indicatorId(indicator))))];
      const key = JSON.stringify([target.yearId, target.branchId, target.schoolId, [...ids].sort()]);
      if (!groups.has(key)) groups.set(key, {
        yearId: target.yearId,
        branchId: target.branchId,
        schoolId: target.schoolId,
        semesters: [],
        indicators: ids,
      });
      groups.get(key).semesters.push(target.semesterId);
    }
    return { groups: [...groups.values()], skipped };
  };

  // Endpoint PUT baru menerima SATU sekolah dan SATU semester per request.
  // Indikator milik list lain tidak ikut dikirim; indikator milik list yang sedang
  // diedit sudah dikecualikan di hasil pengecekan penggunaan.
  // Jangan mengirim array sekolah/semester ke endpoint ini.
  const buildUpdatePlan = () => {
    const requests = [];
    let skipped = 0;
    let skippedTargets = 0;
    for (const target of usageTargets) {
      const usedElsewhere = usageState.indicatorsByTarget[target.key] || [];
      const allowed = chosen.filter((indicator) =>
        !usedElsewhere.some((used) => usedIndicatorMatches(indicator, used))
      );
      skipped += chosen.length - allowed.length;

      // PUT [] berpotensi menghapus data, sehingga tidak boleh dikirim.
      if (allowed.length === 0) {
        skippedTargets += 1;
        continue;
      }
      requests.push({
        yearId: target.yearId,
        semesterId: target.semesterId,
        branchId: target.branchId,
        schoolId: target.schoolId,
        indicatorIds: [...new Set(allowed.map((indicator) => String(indicatorId(indicator))))],
      });
    }
    return { requests, skipped, skippedTargets };
  };

  // POST /api/elements/v2 hanya memerlukan tahun ajaran, cabang, dan sekolah.
  // ID tahun ajaran tetap berasal dari data semester yang dipilih/aktif.
  // Cukup satu request per sekolah, meski dua semester dicentang.
  // Perubahan checkbox semester tidak mengulang GET elemen atau menghapus indikator.
  const elementTargets = useMemo(
    () => academicId && branchId && schoolId && selectedSchool
      ? [{
          yearId: academicId,
          branchId,
          schoolId: selectedSchool.school_code,
        }]
      : [],
    [academicId, branchId, schoolId, selectedSchool]
  );

  useEffect(() => {
    const request = ++elementSequence.current;
    setElements([]);
    setElementId("");
    setIndicators([]);
    setLoadingElement(Boolean(show && elementTargets.length));

    if (!show || !elementTargets.length) return;
    let cancelled = false;

    const fetchElements = async (target) => {
      const pageSize = 100;
      const output = [];
      for (let page = 1; page <= 100; page += 1) {
        const response = await APILA.post("/api/elements/v2", {
          ysb_academic_year_id: target.yearId,
          ysb_branch_id: target.branchId,
          ysb_school_id: target.schoolId,
          page,
          limit: pageSize,
          ascending: true,
          search: "",
          level: "user",
        }, headers);
        const pageData = unpack(response.data);
        output.push(...pageData);

        // Dukung response paginasi yang mengirim total/last_page, sekaligus
        // response array biasa yang hanya mengirim `data`.
        const lastPage = Number(
          response.data?.last_page || response.data?.total_pages ||
          response.data?.meta?.last_page || response.data?.pagination?.last_page || 0
        );
        if (lastPage) {
          if (page >= lastPage) break;
        } else if (pageData.length < pageSize) {
          break;
        }
        if (page === 100) throw new Error("Jumlah data elemen melebihi batas halaman.");
      }
      return output;
    };

    const loadElements = async () => {
      try {
        const results = await Promise.all(elementTargets.map(fetchElements));
        if (cancelled || request !== elementSequence.current) return;

        const uniqueElements = Array.from(new Map(
          results.flat()
            .filter((item) => item?.id || item?.id_element || item?.ysb_element_id)
            .map((item) => [String(item.id || item.id_element || item.ysb_element_id), item])
        ).values()).sort((a, b) =>
          String(a.name_element || a.name || a.element || "").localeCompare(
            String(b.name_element || b.name || b.element || ""), "id", { numeric: true }
          )
        );
        setElements(uniqueElements);
        if (isEdit) {
          const selected = findOption(
            uniqueElements, oldElementId, oldElementName,
            ["id", "id_element", "ysb_element_id"], ["name_element", "name", "element"]
          );
          if (selected) {
            setElementId(String(selected.id || selected.id_element || selected.ysb_element_id));
          }
        }
      } catch (error) {
        if (!cancelled && request === elementSequence.current) {
          ToastError.fire({
            icon: "error",
            title: error.response?.data?.message || "Gagal memuat elemen sesuai sekolah.",
          });
        }
      } finally {
        if (!cancelled && request === elementSequence.current) setLoadingElement(false);
      }
    };

    loadElements();
    return () => { cancelled = true; elementSequence.current += 1; };
  }, [show, elementTargets, headers, isEdit, oldElementId, oldElementName]);

  useEffect(() => {
    const request = ++indicatorSequence.current;
    setIndicators([]);
    setKeyword("");
    setDropdown(false);

    if (!elementId) return;
    setLoadingIndicator(true);

    APILA.get("/api/sub-elements", {
      ...headers,
      params: { id_element: elementId },
    })
      .then((response) => {
        if (request === indicatorSequence.current) {
          const data = unpack(response.data);
          setIndicators(data);
          // Cocokkan indikator lama dengan daftar endpoint agar ID siap untuk payload PUT.
          if (isEdit) {
            setChosen((previous) => previous.map((item) =>
              data.find((option) => sameIndicator(option, item)) || item
            ));
          }
        }
      })
      .catch(() => {
        if (request === indicatorSequence.current) {
          ToastError.fire({ icon: "error", title: "Gagal memuat indikator." });
        }
      })
      .finally(() => {
        if (request === indicatorSequence.current) setLoadingIndicator(false);
      });
  }, [elementId, headers]);

  // Dropdown indikator otomatis menutup saat klik di luar.
  useEffect(() => {
    if (!dropdown) return;
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdown]);

  const toggle = (value, setter) => {
    setter((previous) =>
      previous.includes(value)
        ? previous.filter((item) => item !== value)
        : [...previous, value]
    );
  };

  const filteredIndicators = indicators.filter((indicator) =>
    !chosen.some((item) => sameIndicator(item, indicator)) &&
    indicatorName(indicator).toLowerCase().includes(keyword.toLowerCase())
  );

  const submit = async (event) => {
    event.preventDefault();

    if (isEdit && !updateConfigAnomalyId) {
      ToastError.fire({
        icon: "error",
        title: "ID konfigurasi perilaku tidak ditemukan. Silakan buka ulang modal.",
      });
      return;
    }

    // Modal Update hanya boleh mengirim satu sekolah dan satu semester
    // yang identik dengan baris tabel yang sedang diedit.
    if (isEdit && (
      semesterIds.length !== 1 || !schoolId || usageTargets.length !== 1 ||
      String(semesterIds[0]) !== String(asIds(editData?.ysb_semester_id)[0] || "") ||
      String(branchId) !== String(editData?.ysb_branch_id || "") ||
      String(schoolId) !== String(asIds(editData?.ysb_school_id || editData?.school_code || editData?.school)[0] || "")
    )) {
      ToastError.fire({
        icon: "error",
        title: "Sekolah atau semester data yang diedit tidak ditemukan. Silakan buka ulang modal.",
      });
      return;
    }

    if (!usageReady || usageTargets.length === 0) {
      ToastError.fire({
        icon: "error",
        title: usedLookupError
          ? "Gagal memeriksa penggunaan indikator. Coba buka ulang modal."
          : "Tunggu pemeriksaan indikator tiap sekolah dan semester selesai.",
      });
      return;
    }

    if (
      !academicId ||
      semesterIds.length === 0 ||
      !branchId ||
      !schoolId ||
      loadingSchools ||
      chosen.length === 0 ||
      !description.trim()
    ) {
      ToastError.fire({
        icon: "error",
        title: "Lengkapi semester, sekolah, keterangan dan minimal satu indikator.",
      });
      return;
    }

    if (chosen.some((item) => !indicatorId(item))) {
      ToastError.fire({ icon: "error", title: "Ada indikator tanpa ID. Pilih ulang indikator sebelum menyimpan." });
      return;
    }

    if (!userId) {
      ToastError.fire({
        icon: "error",
        title: "ID pengguna tidak ditemukan. Silakan login ulang.",
      });
      return;
    }

    const plan = isEdit ? buildUpdatePlan() : buildStorePlan();
    if ((isEdit && plan.requests.length === 0) || (!isEdit && plan.groups.length === 0)) {
      ToastError.fire({
        icon: "info",
        title: "Semua indikator sudah digunakan pada sekolah dan semester yang dipilih.",
      });
      return;
    }

    setSaving(true);
    let successCount = 0;
    try {
      if (isEdit) {
        // PUT dieksekusi satu per satu untuk mengisolasi tiap sekolah/semester.
        // Bila salah satu request gagal, tidak melanjutkan ke sekolah lainnya.
        for (const target of plan.requests) {
          await APILA.put("/api/config-anomalys/update-multiple-indikator", {
            ysb_academic_year_id: target.yearId,
            ysb_semester_id: target.semesterId,
            ysb_branch_id: target.branchId,
            ysb_school_id: target.schoolId,
            ysb_config_anomaly_id: updateConfigAnomalyId,
            description_anomaly: description.trim(),
            indikator: target.indicatorIds,
            user_id: userId,
          }, headers);
          successCount += 1;
        }
      } else {
        for (const group of plan.groups) {
          await APILA.post("/api/config-anomalys/store-multiple-indikator", {
            ysb_academic_year_id: group.yearId,
            ysb_semester_id: group.semesters,
            ysb_branch_id: group.branchId,
            ysb_school_id: group.schoolId,
            description_anomaly: description.trim(),
            indikator: group.indicators,
            user_id: userId,
          }, headers);
          successCount += 1;
        }
      }
      ToastSuccess.fire({
        icon: "success",
        title: isEdit
          ? plan.skipped > 0
            ? `${successCount} sekolah/semester diperbarui. ${plan.skipped} indikator terpakai dilewati.`
            : `${successCount} sekolah/semester berhasil diperbarui.`
          : plan.skipped > 0
            ? `List perilaku tersimpan. ${plan.skipped} pemakaian indikator yang sudah ada dilewati.`
            : "List perilaku berhasil ditambahkan.",
      });
      onHide();
      if (onSaved) onSaved();
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title: successCount > 0
          ? `Sebagian data (${successCount} ${isEdit ? "sekolah/semester" : "permintaan"}) tersimpan. ${error.response?.data?.message || "Periksa data lalu coba lagi."}`
          : error.response?.data?.message || "Gagal menyimpan list perilaku.",
      });
      if (successCount > 0) {
        // Refresh tabel dan ulang cek agar retry tidak mengirim duplikat.
        usageCache.current.clear();
        setLookupRevision((previous) => previous + 1);
        if (onSaved) onSaved();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
      dialogClassName="lp-modal-dialog"
      contentClassName="lp-modal-content"
    >
      <form className="lp-modal-form" onSubmit={submit}>
        <Modal.Header className="lp-modal-header">
          <div className="lp-header-info">
            <div className="lp-header-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faList} />
            </div>
            <div>
              <h5 className="lp-modal-title">{isEdit ? "Ubah List Perilaku" : "Tambah List Perilaku"}</h5>
              {/* <p className="lp-modal-subtitle">
                {isEdit
                  ? "Perbarui informasi list perilaku yang dipilih."
                  : "Lengkapi informasi berikut untuk menambahkan list perilaku baru."}
              </p> */}
            </div>
          </div>
          <button
            type="button"
            className="lp-close-button"
            aria-label="Tutup modal"
            onClick={onHide}
            disabled={saving}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </Modal.Header>

        <Modal.Body className="lp-modal-body mt-3">
          {loading ? (
            <div className="lp-loading-form" aria-live="polite">
              <div className="lp-loading-spinner" />
              <span>Memuat data...</span>
            </div>
          ) : (
            <div className="lp-form-layout">
              <section className="lp-field">
                <label className="lp-label" htmlFor="lp-academic-year">
                  {isEdit ? "Tahun Ajaran" : "Tahun Ajaran Aktif"}
                  <FontAwesomeIcon className="lp-lock-icon" icon={faLock} />
                </label>
                <input
                  id="lp-academic-year"
                  className="lp-control lp-year-input"
                  value={year || "Tidak ada tahun ajaran aktif"}
                  readOnly
                />
              </section>

              {isEdit ? (
                <>
                  <section className="lp-field">
                    <div className="lp-field-heading">
                      <span className="lp-label">Semester <FontAwesomeIcon className="lp-lock-icon" icon={faLock} /></span>
                      {/* <p className="lp-help">Semester sesuai data yang dipilih pada tabel.</p> */}
                    </div>
                    <div className="lp-update-target-card">
                      <span className="lp-update-target-icon"><FontAwesomeIcon icon={faGraduationCap} /></span>
                      <span className="lp-update-target-text">
                        <strong>{selectedSemesterInfo ? `Semester ${selectedSemesterInfo.semester}` : "Semester tidak ditemukan"}</strong>
                        <small>{selectedSemesterInfo
                          ? Number(selectedSemesterInfo.semester) === 1 ? "Periode Ganjil" : "Periode Genap"
                          : "Periksa data semester"}</small>
                      </span>
                      <FontAwesomeIcon className="lp-update-target-lock" icon={faLock} title="Tidak dapat diubah" />
                    </div>
                  </section>

                  <section className="lp-field">
                    <div className="lp-field-heading">
                      <span className="lp-label">Sekolah <FontAwesomeIcon className="lp-lock-icon" icon={faLock} /></span>
                      {/* <p className="lp-help">Hanya sekolah dari baris yang sedang diedit.</p> */}
                    </div>
                    <div className="lp-update-target-card">
                      <span className="lp-update-target-icon"><FontAwesomeIcon icon={faSchool} /></span>
                      <span className="lp-update-target-text">
                        <strong>{editSchoolName}{editBranchCode ? ` (${editBranchCode})` : ""}</strong>
                        <small>{editSchoolCode}</small>
                      </span>
                      <FontAwesomeIcon className="lp-update-target-lock" icon={faLock} title="Tidak dapat diubah" />
                    </div>
                  </section>
                </>
              ) : (
                <>
                  <section className="lp-field">
                    <div className="lp-field-heading">
                      <span className="lp-label">
                        Semester <span className="lp-required">*</span>
                      </span>
                      {/* <p className="lp-help">Pilih semester yang berlaku untuk list perilaku ini.</p> */}
                    </div>
                    <div className="lp-semester-grid">
                      {semestersInYear.map((semester) => {
                        const checked = semesterIds.includes(semester.id);
                        const isActive = Number(semester.status) === 1;
                        return (
                          <label
                            key={semester.id}
                            className={`lp-semester-card${checked ? " is-selected" : ""}`}
                          >
                            <input
                              type="checkbox"
                              className="lp-checkbox"
                              checked={checked}
                              onChange={() => {
                                // Checkbox semester hanya mengubah pilihan semester.
                                // Elemen dan indikator yang sudah dipilih tidak
                                // terhapus ketika semester ditambah/dikurangi.
                                toggle(semester.id, setSemesterIds);
                              }}
                            />
                            <span className="lp-semester-icon" aria-hidden="true">
                              <FontAwesomeIcon icon={faGraduationCap} />
                            </span>
                            <span className="lp-semester-details">
                              <strong>
                                Semester {semester.semester}
                                {isActive && <span className="lp-active-text"> (Aktif)</span>}
                              </strong>
                              <small>
                                {Number(semester.semester) === 1
                                  ? "Periode Ganjil"
                                  : "Periode Genap"}
                              </small>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>

                  <section className="lp-field">
                    {/* <div className="lp-field-heading">
                      <span className="lp-label">Lokasi Sekolah <span className="lp-required">*</span></span>
                      <p className="lp-help">Pilih cabang terlebih dahulu, kemudian satu sekolah pada cabang tersebut.</p>
                    </div> */}
                    <div className="lp-school-filter-grid">
                      <div>
                        <label className="lp-label" htmlFor="lp-branch">Cabang <span className="lp-required">*</span></label>
                        <div className="lp-select-wrap">
                          <select
                            id="lp-branch"
                            className="lp-control lp-select"
                            value={branchId}
                            onChange={(event) => {
                              setBranchId(event.target.value);
                              setSchoolId("");
                              setSchools([]);
                            }}
                            disabled={saving}
                            required
                          >
                            <option value="">Pilih Cabang</option>
                            {branches.map((branch) => (
                              <option key={branch.branch_code} value={branch.branch_code}>
                                {branch.branch_name || branch.branch_code}
                              </option>
                            ))}
                          </select>
                          <FontAwesomeIcon className="lp-select-arrow" icon={faChevronDown} />
                        </div>
                      </div>
                      <div>
                        <label className="lp-label" htmlFor="lp-school">Sekolah <span className="lp-required">*</span></label>
                        <div className="lp-select-wrap">
                          <select
                            id="lp-school"
                            className="lp-control lp-select"
                            value={schoolId}
                            onChange={(event) => setSchoolId(event.target.value)}
                            disabled={!branchId || loadingSchools || saving}
                            required
                          >
                            <option value="">
                              {!branchId ? "Pilih Cabang terlebih dahulu" : loadingSchools
                                ? "Memuat sekolah..." : visibleSchools.length
                                ? "Pilih Sekolah" : "Belum ada sekolah"}
                            </option>
                            {visibleSchools.map((school) => (
                              <option key={school.school_code} value={school.school_code}>
                                {school.school_name}
                              </option>
                            ))}
                          </select>
                          <FontAwesomeIcon className="lp-select-arrow" icon={faChevronDown} />
                        </div>
                      </div>
                    </div>
                  </section>
                </>
              )}

              <section className="lp-field">
                <div className="lp-field-heading">
                  <label className="lp-label" htmlFor="lp-elemen">
                    Elemen <span className="lp-required">*</span>
                  </label>
                  <p className="lp-help">Pilih elemen untuk menampilkan daftar indikator.</p>
                </div>
                <div className="lp-select-wrap">
                  <select
                    id="lp-elemen"
                    className="lp-control lp-select"
                    value={elementId}
                    onChange={(event) => {
                      // Ganti elemen tanpa menghapus indikator yang sudah dipilih.
                      setElementId(event.target.value);
                    }}
                    disabled={loadingElement || !elementTargets.length}
                  >
                    <option value="">
                      {!elementTargets.length
                        ? "Pilih sekolah terlebih dahulu"
                        : loadingElement
                        ? "Memuat elemen..."
                        : elements.length
                        ? "Pilih Elemen"
                        : "Belum ada elemen"}
                    </option>
                    {elements.map((element) => (
                      <option
                        key={element.id || element.id_element || element.ysb_element_id}
                        value={element.id || element.id_element || element.ysb_element_id}
                      >
                        {element.description || "Elemen"}
                      </option>
                    ))}
                  </select>
                  <FontAwesomeIcon className="lp-select-arrow" icon={faChevronDown} />
                </div>
              </section>

              <section className="lp-field">
                <div className="lp-field-heading">
                  <label className="lp-label" htmlFor="lp-indicator-search">
                    Indikator <span className="lp-required">*</span>
                  </label>
                  {/* <p className="lp-help">{isEdit
                    ? "Pilih indikator untuk sekolah dan semester di atas. Indikator dari list lain tidak dapat dipilih."
                    : "Indikator yang sudah digunakan pada semester tertentu akan dilewati hanya untuk semester tersebut."}</p> */}
                </div>

                {chosen.length > 0 && (
                  <div className="lp-chosen-indicators">
                    {chosen.map((indicator, index) => {
                      const id = indicatorId(indicator);
                      return (
                        <span className="lp-indicator-chip" key={id || index}>
                          <span>{indicatorName(indicator)}</span>
                          <button
                            type="button"
                            aria-label={`Hapus ${indicatorName(indicator)}`}
                            onClick={() =>
                              setChosen((previous) =>
                                previous.filter((item) => !sameIndicator(item, indicator))
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {chosen.length > 0 && usageReady && (() => {
                  const plan = isEdit ? buildUpdatePlan() : buildStorePlan();
                  return plan.skipped > 0 ? (
                    <p className="lp-partial-summary" role="status">
                      {plan.skipped} kombinasi indikator/sekolah/semester sudah digunakan di list lain dan akan dilewati saat menyimpan.
                    </p>
                  ) : null;
                })()}
                {/* {isEdit && <p className="lp-help">
                  Perubahan hanya berlaku untuk sekolah dan semester yang ditampilkan di atas.
                </p>} */}

                <div className="lp-indicator-picker" ref={dropdownRef}>
                  <div className="lp-indicator-input-wrap">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="lp-search-icon" />
                    <input
                      id="lp-indicator-search"
                      className="lp-control lp-indicator-input"
                      placeholder={
                        !elementId
                          ? "Pilih elemen terlebih dahulu"
                          : loadingIndicator
                          ? "Memuat indikator..."
                          : !usageReady && !usedLookupError
                          ? "Memeriksa indikator tiap sekolah/semester..."
                          : usedLookupError
                          ? "Gagal memeriksa penggunaan indikator"
                          : "Cari dan pilih indikator..."
                      }
                      value={keyword}
                      disabled={!elementId || loadingIndicator || !usageReady}
                      onChange={(event) => {
                        setKeyword(event.target.value);
                        setDropdown(true);
                      }}
                      onFocus={() => setDropdown(true)}
                    />
                    <button
                      type="button"
                      className="lp-indicator-toggle"
                      aria-label="Tampilkan indikator"
                      disabled={!elementId || loadingIndicator || !usageReady}
                      onClick={() => setDropdown((previous) => !previous)}
                    >
                      <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                  </div>

                  {usedLookupError && <p className="lp-indicator-check-error">
                    Data penggunaan indikator gagal dimuat. Tutup dan buka kembali modal untuk mencoba lagi.
                  </p>}
                  {dropdown && elementId && !loadingIndicator && usageReady && (
                    <div className="lp-indicator-options">
                      {filteredIndicators.length > 0 ? (
                        filteredIndicators.map((indicator) => {
                          const usage = getIndicatorUsage(indicator);
                          const allUsed = usage.disabled;
                          const partlyUsed = usage.used > 0 && !allUsed;
                          const status = `${usage.used} dari ${usage.total} sekolah/semester`;
                          return (
                            <button
                              type="button"
                              className={`lp-indicator-option${allUsed ? " is-used" : partlyUsed ? " is-partly-used" : ""}`}
                              key={indicatorId(indicator)}
                              disabled={allUsed}
                              title={usage.used > 0
                                ? `Terpakai: ${usage.usedTargets.map((target) => `${target.schoolId} - Semester ${semestersInYear.find((s) => String(s.id) === String(target.semesterId))?.semester || "?"}`).join(", ")}`
                                : "Belum digunakan pada sekolah/semester terpilih"}
                              onClick={() => {
                                if (allUsed) return;
                                setChosen((previous) => [...previous, indicator]);
                                setKeyword("");
                                setDropdown(false);
                              }}
                            >
                              <span className="lp-indicator-option-name">{indicatorName(indicator)}</span>
                              {allUsed ? (
                                <span className="lp-used-label">
                                  <FontAwesomeIcon icon={faLock} /> {isEdit ? "Sudah digunakan" : `Sudah digunakan semua (${status})`}
                                </span>
                              ) : partlyUsed ? (
                                <span className="lp-partial-used-label">{isEdit ? "Sudah digunakan" : `Sudah digunakan ${status}`}</span>
                              ) : null}
                            </button>
                          );
                        })
                      ) : (
                        <p className="lp-empty-indicators">
                          Tidak ada indikator yang tersedia.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className="lp-field">
                <div className="lp-field-heading">
                  <label className="lp-label" htmlFor="lp-description">
                    Keterangan Perilaku <span className="lp-required">*</span>
                  </label>
                  {/* <p className="lp-help">
                    Berikan keterangan atau deskripsi singkat mengenai perilaku ini.
                  </p> */}
                </div>
                <textarea
                  id="lp-description"
                  className="lp-control lp-description"
                  rows={3}
                  maxLength={500}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Masukkan keterangan perilaku..."
                  required
                />
                <span className="lp-char-count">{description.length}/500</span>
              </section>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="lp-modal-footer">
          <button
            type="button"
            className="lp-button lp-button-cancel"
            onClick={onHide}
            disabled={saving}
          >
            Batal
          </button>
          <button
            type="submit"
            className="lp-button lp-button-save"
            disabled={saving || loading}
          >
            <FontAwesomeIcon icon={faFloppyDisk} />
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </Modal.Footer>
      </form>
      <style>{`
        .lp-school-filter-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
        .lp-school-filter-grid > div { min-width: 0; }
        .lp-school-filter-grid .lp-label { display: block; margin-bottom: 7px; }
        .lp-school-filter-grid .lp-select { width: 100%; }
        @media (max-width: 600px) { .lp-school-filter-grid { grid-template-columns: 1fr; } }
      `}</style>
    </Modal>
  );
}
